import { ThemeOptions, createTheme } from "@mui/material/styles";
import { deepmerge } from "@mui/utils";
import type {} from "@mui/x-date-pickers/themeAugmentation";

import common from "../i18n/de/common";
import { filterDropdownOptions } from "../utils/filterDropdownOptions";
import { MuiAlert } from "./components/MuiAlert";
import { MuiChip } from "./components/MuiChip";
import { MuiInputBase } from "./components/MuiInputBase";
import { MuiToggleButton } from "./components/MuiToggleButton";
import soneuTheme from "./soneu-theme-converted.json";

// Silence type errors for custom type definitions from Figma. These are then referenced in theme.
declare module "@mui/material/styles" {
  interface CommonColors {
    white_states?: {
      main: string;
      focusVisible: string;
    };
  }

  interface TypeText {
    _states?: {
      hover?: string;
      selected?: string;
      focus?: string;
      focusVisible?: string;
      outlinedBorder?: string;
    };
  }

  interface SimplePaletteColorOptions {
    _states?: {
      hover?: string;
      selected?: string;
      focus?: string;
      focusVisible?: string;
      outlinedBorder?: string;
    };
  }

  interface Palette {
    fsw: {
      yellow: string;
      "oragen-wcag": string;
      red: string;
      green: string;
      blue: string;
      darkblue: string;
      darkerblue: string;
      "blue-5": string;
      "blue-10": string;
      "blue-25": string;
      "blue-50": string;
      "blue-80": string;
      grey: string;
      "grey-25": string;
      "grey-50": string;
    };
  }

  interface PaletteOptions {
    _components?: {
      switch?: {
        knobFillEnabled?: string;
        slideFill?: string;
      };
    };
    fsw: {
      yellow: string;
      "oragen-wcag": string;
      red: string;
      green: string;
      blue: string;
      darkblue: string;
      darkerblue: string;
      "blue-5": string;
      "blue-10": string;
      "blue-25": string;
      "blue-50": string;
      "blue-80": string;
      grey: string;
      "grey-25": string;
      "grey-50": string;
    };
  }
}

const INPUT_FONT_SIZE = "0.875rem";

const muiConfig: ThemeOptions = {
  cssVariables: true, // This generates the var(--mui-...) strings
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1280,
      xl: 1536,
    },
  },
  colorSchemes: {
    dark: false,
  },
  components: {
    MuiToggleButton,
    MuiAlert,
    MuiChip,
    MuiInputBase,
    MuiAutocomplete: {
      defaultProps: {
        filterOptions: filterDropdownOptions,
        noOptionsText: common.noEntriesFound,
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        outlined: {
          fontSize: INPUT_FONT_SIZE,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: INPUT_FONT_SIZE,
        },
        input: {
          minHeight: "24px",
          lineHeight: "24px",
          padding: "8px 14px",
          fontSize: INPUT_FONT_SIZE,
          "&.MuiInputBase-inputMultiline": {
            padding: 0,
          },
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: INPUT_FONT_SIZE,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "0.625rem",
          lineHeight: 1.4,
          marginTop: 2,
        },
      },
    },
  },
};

const mergedOptions = deepmerge(soneuTheme, muiConfig);

export const appTheme = createTheme(mergedOptions);
